import { C } from "../core/config.js";
import { htmlParaMostrar, normalizarTextoRico } from "../core/textoRico.js";

// ─── Editor de texto con formato ─────────────────────────────────────────────
// Un campo de notas que permite resaltar con el mouse un trozo del texto y
// ponerlo en negrilla, subrayado, de otro color o de otro tamaño. Solo cambia
// lo seleccionado; el resto del texto queda igual.
//
// Dos detalles que hacen que esto funcione de verdad:
//
// 1. Al hacer clic en un botón de la barra, el navegador le quita el foco al
//    texto y con él la selección. Por eso los botones cancelan su `mousedown`
//    (no roban el foco) y, además, el editor guarda el último rango
//    seleccionado para restaurarlo antes de aplicar el formato — el <select>
//    de tamaño sí necesita el foco para abrirse, y sin el rango guardado
//    aplicaría el cambio en el vacío.
//
// 2. `render()` reconstruye todo el DOM. El valor vive en `state`, así que la
//    barra no guarda estado propio: se vuelve a pintar desde el mismo HTML.

const COLORES = [
  ["#1f2937", "Negro"],
  ["#c0392b", "Rojo"],
  ["#2B4A82", "Azul"],
  ["#15803d", "Verde"],
  ["#b45309", "Naranja"]
];

// El <font size> clásico: es lo que emite el navegador con execCommand
// ("fontSize") y `sanitizarTextoRico` lo traduce a un tamaño en píxeles.
const TAMANOS = [
  ["", "Tamaño"],
  ["2", "Pequeño"],
  ["3", "Normal"],
  ["5", "Grande"],
  ["6", "Muy grande"]
];

function btnBarra(contenido, titulo, attrs, extra = "") {
  return `<button type="button" ${attrs} title="${titulo}" aria-label="${titulo}"
    style="min-width:26px;height:26px;padding:0 6px;border:1px solid ${C.border};background:white;border-radius:5px;
           cursor:pointer;font-size:12px;line-height:1;color:${C.text};display:inline-flex;align-items:center;
           justify-content:center;${extra}">${contenido}</button>`;
}

// HTML del editor completo (barra + área editable) para un campo del documento.
export function editorRicoHTML({ campo, valor, alto = 78, ayuda = "" }) {
  const swatches = COLORES.map(([hex, nombre]) =>
    btnBarra("", `Color ${nombre}`, `data-rico-color="${hex}"`,
      `background:${hex};border-color:${hex};min-width:18px;width:18px;height:18px;border-radius:50%;`)
  ).join("");

  return `
    <div data-rico-caja="${campo}" style="border:1px solid ${C.border};border-radius:6px;overflow:hidden;background:white;">
      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:5px;padding:5px 6px;background:${C.bg};border-bottom:1px solid ${C.border};">
        ${btnBarra("N", "Negrilla", `data-rico-cmd="bold"`, "font-weight:800;")}
        ${btnBarra("K", "Cursiva", `data-rico-cmd="italic"`, "font-style:italic;")}
        ${btnBarra("S", "Subrayado", `data-rico-cmd="underline"`, "text-decoration:underline;")}
        <span style="width:1px;height:18px;background:${C.border};"></span>
        <select data-rico-tam title="Tamaño del texto seleccionado"
          style="height:26px;border:1px solid ${C.border};border-radius:5px;background:white;font-size:11px;color:${C.text};cursor:pointer;">
          ${TAMANOS.map(([v, t]) => `<option value="${v}">${t}</option>`).join("")}
        </select>
        <span style="width:1px;height:18px;background:${C.border};"></span>
        <span style="display:inline-flex;gap:4px;align-items:center;">${swatches}</span>
        ${btnBarra("🖍", "Resaltar en amarillo", `data-rico-fondo="#fde68a"`)}
        <span style="width:1px;height:18px;background:${C.border};"></span>
        ${btnBarra("✕", "Quitar formato", `data-rico-cmd="removeFormat"`, `color:${C.muted};`)}
      </div>
      <div data-rico="${campo}" contenteditable="true" spellcheck="true"
        style="min-height:${alto}px;max-height:220px;overflow-y:auto;padding:8px 10px;font-size:12.5px;
               line-height:1.5;color:${C.text};outline:none;white-space:pre-wrap;word-break:break-word;"
      >${htmlParaMostrar(valor)}</div>
    </div>
    ${ayuda ? `<div style="font-size:10px;color:${C.muted};margin-top:4px;">${ayuda}</div>` : ""}
  `;
}

function editableDe(el) {
  const caja = el.closest("[data-rico-caja]");
  return caja ? caja.querySelector("[data-rico]") : null;
}

function guardarRango(ed) {
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const r = sel.getRangeAt(0);
  if (ed.contains(r.commonAncestorContainer)) ed._rangoRico = r.cloneRange();
}

function restaurarRango(ed) {
  ed.focus();
  const sel = window.getSelection && window.getSelection();
  if (!sel) return;
  if (ed._rangoRico) {
    sel.removeAllRanges();
    sel.addRange(ed._rangoRico);
  }
}

function ejecutar(ed, cmd, valor, conCSS) {
  restaurarRango(ed);
  try { document.execCommand("styleWithCSS", false, !!conCSS); } catch { /* no en todos los navegadores */ }
  try { document.execCommand(cmd, false, valor); } catch { /* comando no soportado */ }
  guardarRango(ed);
}

// Conecta todos los editores presentes en el DOM. `onCambio(campo, valor)` se
// llama con el valor ya saneado, listo para guardar en el documento.
export function attachEditoresRicos(onCambio) {
  document.querySelectorAll("[data-rico]").forEach(ed => {
    const campo = ed.dataset.rico;
    const avisar = () => onCambio(campo, normalizarTextoRico(ed.innerHTML));

    ed.addEventListener("input", avisar);
    ed.addEventListener("blur", () => { guardarRango(ed); avisar(); });
    ed.addEventListener("keyup", () => guardarRango(ed));
    ed.addEventListener("mouseup", () => guardarRango(ed));

    // Pegar desde Word o desde una página trae HTML entero: se pega el texto
    // plano y punto. El formato lo pone el usuario con la barra.
    ed.addEventListener("paste", e => {
      const texto = (e.clipboardData || window.clipboardData).getData("text/plain");
      e.preventDefault();
      try { document.execCommand("insertText", false, texto); } catch { /* raro, pero no rompe */ }
      avisar();
    });
  });

  const alAplicar = (el, fn) => {
    el.addEventListener("mousedown", ev => ev.preventDefault());  // no robar el foco
    el.addEventListener("click", ev => {
      ev.preventDefault();
      ev.stopPropagation();
      const ed = editableDe(el);
      if (!ed) return;
      fn(ed);
      onCambio(ed.dataset.rico, normalizarTextoRico(ed.innerHTML));
    });
  };

  // Negrilla, cursiva y subrayado se piden SIN styleWithCSS: así el navegador
  // escribe <b>, <i> y <u> —etiquetas cortas, estables y fáciles de imprimir—
  // en vez de spans con estilo.
  document.querySelectorAll("[data-rico-cmd]").forEach(b =>
    alAplicar(b, ed => ejecutar(ed, b.dataset.ricoCmd, null, false)));

  document.querySelectorAll("[data-rico-color]").forEach(b =>
    alAplicar(b, ed => ejecutar(ed, "foreColor", b.dataset.ricoColor, true)));

  document.querySelectorAll("[data-rico-fondo]").forEach(b =>
    alAplicar(b, ed => {
      restaurarRango(ed);
      try { document.execCommand("styleWithCSS", false, true); } catch { /* ignorar */ }
      // Firefox solo entiende hiliteColor; Chrome acepta ambos.
      try { document.execCommand("hiliteColor", false, b.dataset.ricoFondo); }
      catch { try { document.execCommand("backColor", false, b.dataset.ricoFondo); } catch { /* ignorar */ } }
      guardarRango(ed);
    }));

  document.querySelectorAll("[data-rico-tam]").forEach(sel => {
    sel.addEventListener("change", ev => {
      ev.stopPropagation();
      const ed = editableDe(sel);
      const v = sel.value;
      sel.value = "";                     // la barra no guarda estado
      if (!ed || !v) return;
      // Sin styleWithCSS el navegador emite <font size="1..7">, que el
      // saneador traduce a un tamaño en píxeles conocido.
      ejecutar(ed, "fontSize", v, false);
      onCambio(ed.dataset.rico, normalizarTextoRico(ed.innerHTML));
    });
  });
}
